import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  SuiModal,
  ComponentModalConfig,
  ModalSize,
} from '@richardlt/ng2-semantic-ui';
import { ApplicationSecurityService } from '../../../components/application-security/services/application-security.service';

interface EditFileSizeModalContext {
  title: string;
  data: any;
}
@Component({
  selector: 'app-edit-file-size-modal',
  templateUrl: './edit-file-size-modal.component.html',
  styleUrls: ['./edit-file-size-modal.component.scss'],
})
export class EditFileSizeModalComponent implements OnInit {
  pending: boolean = false;
  fileUploadForm: FormGroup = this.fb.group({});
  formSubmitAttempt: boolean;
  error: null;
  success: null;
  data = this.modal.context.data;
  constructor(
    private appSecuritySvc: ApplicationSecurityService,
    @Inject(DOCUMENT) private document,
    private fb: FormBuilder,
    public modal: SuiModal<EditFileSizeModalContext, string, void>
  ) {}

  ngOnInit(): void {
    this.setupFormFields();
  }

  setupFormFields() {
    // Maximum File Size
    if (this.data.fileSettingsId === 'file_size_configuration') {
      this.fileUploadForm.addControl(
        'imageMaxSize',
        this.fb.control('', [Validators.required])
      );
      this.fileUploadForm.addControl(
        'videoMaxSize',
        this.fb.control('', [Validators.required])
      );
      this.fileUploadForm.addControl(
        'documentMaxSize',
        this.fb.control('', [Validators.required])
      );
      this.fileUploadForm.addControl(
        'otherMaxSize',
        this.fb.control('', [Validators.required])
      );
    }

    // Allowed File Types
    if (this.data.fileSettingsId === 'allowed_file_types') {
      this.fileUploadForm.addControl(
        'imageFileTypes',
        this.fb.control('', [Validators.required])
      );
      this.fileUploadForm.addControl(
        'videoFileTypes',
        this.fb.control('', [Validators.required])
      );
      this.fileUploadForm.addControl(
        'documentFileTypes',
        this.fb.control('', [Validators.required])
      );
      this.fileUploadForm.addControl(
        'otherFileTypes',
        this.fb.control('', [Validators.required])
      );
    }

    // File Upload Feedback
    if (this.data.fileSettingsId === 'file_upload_response') {
      this.fileUploadForm.addControl(
        'maxFileSizeExceeded',
        this.fb.control('', [Validators.required])
      );
      this.fileUploadForm.addControl(
        'unallowedFileType',
        this.fb.control('', [Validators.required])
      );
    }

    // Concurrent Upload Limits
    if (this.data.fileSettingsId === 'file_concurrent_uploads') {
      this.fileUploadForm.addControl(
        'count',
        this.fb.control('', [Validators.required])
      );

      // this.fileUploadForm.patchValue({
      //   count: JSON.parse(this.data.settings).count,
      // });
    }
    this.fileUploadForm.patchValue(JSON.parse(this.data.settings));
  }

  ngOnDestroy(): void {
    this.document.body.classList.add('panel');
  }

  isFieldValid(field: string) {
    return (
      (!this.fileUploadForm.get(field).valid &&
        this.fileUploadForm.get(field).touched) ||
      (this.fileUploadForm.get(field).untouched && this.formSubmitAttempt)
    );
  }

  displayFieldCss(field: string) {
    return {
      error: this.isFieldValid(field),
    };
  }

  displayFormCss(field: string) {
    return {
      loading: field,
    };
  }

  nullify() {
    this.error = null;
    this.success = null;
  }

  save() {
    this.nullify();
    this.formSubmitAttempt = false;

    console.log(this.data, ' file data ', this.fileUploadForm.value);
    let formBody = this.fileUploadForm.value;

    this.formatFileTypesIntoArrays(formBody);

    if (this.fileUploadForm.valid) {
      this.pending = true;
      switch (this.data.fileSettingsId) {
        case 'file_size_configuration':
          this.editFileSize(formBody);
          break;

        case 'allowed_file_types':
          this.editFileTypes(formBody);
          break;

        case 'file_upload_response':
          this.editResponseMessage(formBody);
          break;

        case 'file_concurrent_uploads':
          this.editConcurrentUpload(formBody);
          break;

        default:
          break;
      }
      return;
    }
  }

  formatFileTypesIntoArrays(formBody) {
    if (this.data?.fileSettingsId === 'allowed_file_types') {
      const imageFileTypes = this.fileUploadForm.get('imageFileTypes')?.value;
      formBody.imageFileTypes = Array.isArray(imageFileTypes)
        ? imageFileTypes.map((item) => item.trim()) // If it's already an array, just trim each item
        : imageFileTypes.split(',').map((item) => item.trim()); // Otherwise, split and trim

      const documentFileTypes =
        this.fileUploadForm.get('documentFileTypes')?.value;
      formBody.documentFileTypes = Array.isArray(documentFileTypes)
        ? documentFileTypes.map((item) => item.trim())
        : documentFileTypes.split(',').map((item) => item.trim());

      const videoFileTypes = this.fileUploadForm.get('videoFileTypes')?.value;
      formBody.videoFileTypes = Array.isArray(videoFileTypes)
        ? videoFileTypes.map((item) => item.trim())
        : videoFileTypes.split(',').map((item) => item.trim());

      const otherFileTypes = this.fileUploadForm.get('otherFileTypes')?.value;
      formBody.otherFileTypes = Array.isArray(otherFileTypes)
        ? otherFileTypes.map((item) => item.trim())
        : otherFileTypes.split(',').map((item) => item.trim());
    }
  }

  editConcurrentUpload(formBody: any): void {
    this.pending = true;
    this.appSecuritySvc
      .updateFileConcurrentLimit({
        body: formBody,
      })
      .subscribe(
        (res: any) => {
          this.pending = false;

          this.success = res && res.message ? res.message : 'Update Successful';
          setTimeout(() => {
            this.modal.approve('approved');
          }, 700);
        },
        (error) => {
          this.pending = false;
          this.error = error.error.errorMessage
            ? error.error.errorMessage
            : error.error
            ? error.error
            : error;
        }
      );
  }

  editFileSize(formBody: any): void {
    this.pending = true;
    this.appSecuritySvc
      .updateFileSize({
        body: formBody,
      })
      .subscribe(
        (res: any) => {
          this.pending = false;

          this.success = res && res.message ? res.message : 'Update Successful';
          setTimeout(() => {
            this.modal.approve('approved');
          }, 700);
        },
        (error) => {
          this.pending = false;
          this.error = error.error.errorMessage
            ? error.error.errorMessage
            : error.error
            ? error.error
            : error;
        }
      );
  }

  editFileTypes(formBody: any): void {
    this.pending = true;
    this.appSecuritySvc
      .updateFileTypes({
        body: formBody,
      })
      .subscribe(
        (res: any) => {
          this.pending = false;
          this.success = res && res.message ? res.message : 'Update Successful';

          setTimeout(() => {
            this.modal.approve('approved');
          }, 700);
        },
        (error) => {
          this.pending = false;
          this.error = error.error.errorMessage
            ? error.error.errorMessage
            : error.error
            ? error.error
            : error;
        }
      );
  }

  editResponseMessage(formBody: any): void {
    this.pending = true;
    this.appSecuritySvc
      .updateFileUploadMaxSize({
        body: formBody,
      })
      .subscribe(
        (res: any) => {
          this.pending = false;
          this.success = res && res.message ? res.message : 'Update Successful';

          setTimeout(() => {
            this.modal.approve('approved');
          }, 700);
        },
        (error) => {
          this.pending = false;
          this.error = error.error.errorMessage
            ? error.error.errorMessage
            : error.error
            ? error.error
            : error;
        }
      );
  }
  enableOnSave(item) {}
}

export class EditFileSizeModal extends ComponentModalConfig<
  EditFileSizeModalContext,
  void,
  void
> {
  constructor(title: string, data: any, size: ModalSize = ModalSize.Mini) {
    super(EditFileSizeModalComponent, {
      title,
      data,
    });

    this.isClosable = false;
    this.transitionDuration = 200;
    this.size = size;
    this.isFullScreen = false;
    this.isInverted = false;
  }
}
